import {
  Button, IconButton, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Chip
} from "@mui/material";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { CiLink } from "react-icons/ci";
import { CiCirclePlus } from "react-icons/ci";
import { useEffect, useState } from "react";
import { ApiRequest } from "GlobalFunctions";
import Box from "elements/Box";
import { useSnackbar } from 'notistack';
import AlertDialog from "elements/AlertDialog";

const emptyForm = { from_url: '', to_url: '' };

export default function RedirectsList(props: any) {
  const [list, setList] = useState<any[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const loadList = () => {
    ApiRequest({
      url: '/redirects/list',
      method: 'GET',
      setResponse: (r: any) => {
        if (r?.status === 'success') setList(r.list);
      }
    });
  };

  useEffect(() => { loadList(); }, []);

  const handleAdd = async () => {
    if (!form.from_url.trim() || !form.to_url.trim()) {
      enqueueSnackbar('Debes completar ambas URLs', { variant: 'warning' });
      return;
    }
    const from = form.from_url.trim().startsWith('/')
      ? form.from_url.trim()
      : '/' + form.from_url.trim();
    const to = form.to_url.trim().startsWith('/')
      ? form.to_url.trim()
      : '/' + form.to_url.trim();

    setSaving(true);
    await ApiRequest({
      url: '/redirects/save',
      method: 'POST',
      body: { from_url: from, to_url: to },
      setResponse: (r: any) => {
        if (r?.status === 'success') {
          enqueueSnackbar('Redirección guardada y activada', { variant: 'success' });
          setForm(emptyForm);
          loadList();
        } else {
          enqueueSnackbar('Error al guardar', { variant: 'error' });
        }
        setSaving(false);
      }
    });
  };

  const handleToggle = async (redirect_id: number, is_active: number) => {
    setList(prev => prev.map(r => r.redirect_id === redirect_id ? { ...r, is_active: is_active ? 0 : 1 } : r));
    await ApiRequest({
      url: '/redirects/save',
      method: 'POST',
      body: { redirect_id, is_active: is_active ? 0 : 1 },
      setResponse: () => { loadList(); }
    });
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await ApiRequest({
      url: '/redirects/delete',
      method: 'POST',
      body: { redirect_id: deleteId },
      setResponse: (r: any) => {
        if (r?.status === 'success') {
          enqueueSnackbar('Redirección eliminada', { variant: 'success' });
          loadList();
        }
      }
    });
    setDeleteId(null);
  };

  return (
    <>
      <div className="content">
        <div className='blog_title_row'>
          <h2 className='titles'><CiLink size={25} /> Redirecciones 301</h2>
        </div>

        <Box style={{ marginBottom: 24 }}>
          <p style={{ margin: '0 0 16px', fontSize: 14, color: '#555' }}>
            Agrega redirecciones para mantener el SEO cuando cambias una URL. El servidor enviará un HTTP 301 automáticamente.
          </p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="URL antigua  (ej: /blog/mi-articulo-viejo)"
              value={form.from_url}
              onChange={e => setForm(f => ({ ...f, from_url: e.target.value }))}
              size="small"
              style={{ flex: 1, minWidth: 240 }}
            />
            <span style={{ color: '#888', fontWeight: 600 }}>→</span>
            <TextField
              label="URL nueva  (ej: /blog/mi-articulo)"
              value={form.to_url}
              onChange={e => setForm(f => ({ ...f, to_url: e.target.value }))}
              size="small"
              style={{ flex: 1, minWidth: 240 }}
            />
            <Button
              variant="contained"
              startIcon={<CiCirclePlus />}
              onClick={handleAdd}
              disabled={saving}
            >
              Agregar
            </Button>
          </div>
        </Box>

        <Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>URL antigua (origen)</TableCell>
                  <TableCell>URL nueva (destino)</TableCell>
                  <TableCell align="center">Activa</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" style={{ color: '#888', padding: 32 }}>
                      No hay redirecciones configuradas
                    </TableCell>
                  </TableRow>
                )}
                {list.map((item: any) => (
                  <TableRow key={item.redirect_id}>
                    <TableCell>
                      <code style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: 4, fontSize: 13 }}>
                        {item.from_url}
                      </code>
                    </TableCell>
                    <TableCell>
                      <code style={{ background: '#e8f5e9', padding: '2px 6px', borderRadius: 4, fontSize: 13 }}>
                        {item.to_url}
                      </code>
                    </TableCell>
                    <TableCell align="center">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <Switch
                          checked={!!item.is_active}
                          onChange={() => handleToggle(item.redirect_id, item.is_active)}
                          size="small"
                        />
                        <Chip
                          label={item.is_active ? 'Activa' : 'Inactiva'}
                          size="small"
                          color={item.is_active ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </div>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="error"
                        onClick={() => setDeleteId(item.redirect_id)}
                      >
                        <DeleteOutlineOutlinedIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </div>

      <AlertDialog
        open={deleteId !== null}
        setOpen={(v: boolean) => { if (!v) setDeleteId(null); }}
        subtitle="¿Eliminar esta redirección? Se quitará del servidor inmediatamente."
        send={handleDelete}
      />
    </>
  );
}
